import './CustomQuestionSection.scss';
import * as Yup from 'yup';
import {
  CUSTOM_QUESTION_TYPE,
  LANGUAGES,
  QUESTION_CHOICE_LIMIT_TYPE
} from '../../utils/constants';
import {
  CustomQuestion,
  SurveyCustomQuestionChoice
} from '../../models/survey';

import { useEffect, useMemo } from 'react';

import Checkboxes from '../Checkboxes/Checkboxes';
import { FiAlertTriangle } from 'react-icons/fi';
import { Form } from 'react-bootstrap';
import SelectQuestion from '../SelectQuestion/SelectQuestion';
import YesNoButton from '../YesNoButton/YesNoButton';
import icon from '../../assets/images/ubisoft-playtest-icon-custom-q-fire.webp';
import siteMessages from '../../pages/root/siteMessages';
import { useIntl } from 'react-intl';

interface Props {
  title: string;
  values: any;
  currentLanguageId: number;
  questions?: CustomQuestion[];
  errors?: any;
  updateSchema: (value: any) => void;
  setFieldValue: (field: string, value: any) => void;
}

const MAX_MULTIPLE_CHOICE_THRESHOLD = 6;
const MIN_CHAR_OPEN_ENDED_QUESTION = 3;
const MAX_CHAR_OPEN_ENDED_QUESTION = 20000;

const CustomQuestionSection = ({
  title,
  values,
  currentLanguageId,
  questions,
  errors,
  updateSchema,
  setFieldValue
}: Props) => {
  const intl = useIntl();
  const orderedQuestions = useMemo(() => {
    return [...(questions ?? [])].sort((a, b) => a.orderIndex - b.orderIndex);
  }, [questions]);

  const buildOptions = (choices: SurveyCustomQuestionChoice[] = []) => {
    const localizationLanguageId = LANGUAGES.find(
      (l) => l.id === currentLanguageId
    )?.localizationLanguageId;

    const orderedChoices = [...choices].sort(
      (a, b) => a.orderIndex - b.orderIndex
    );

    return orderedChoices.map((choice: SurveyCustomQuestionChoice) => {
      const label = choice?.surveyQuestionChoiceLanguages?.find(
        (cl: any) => cl.languageId === localizationLanguageId
      )?.choice;

      return {
        value: choice?.id ?? '',
        label: String(label)
      };
    });
  };

  useEffect(() => {
    if (questions?.length) {
      const candidateSurveyCustomAnswerSchema = questions.reduce(
        (schema: any, q: CustomQuestion) => {
          if (q.id) {
            if (
              q.customQuestionTypeId === CUSTOM_QUESTION_TYPE.BINARY &&
              q.isMandatory
            ) {
              schema[q.id] = Yup.object({
                answeredYes: Yup.boolean().required(
                  intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
                )
              });
            } else if (
              q.customQuestionTypeId === CUSTOM_QUESTION_TYPE.SINGLE &&
              q.isMandatory
            ) {
              schema[q.id] = Yup.object({
                surveyCustomQuestionChoiceIds: Yup.mixed()
                  .test(
                    'is-valid-single',
                    intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }),
                    function (value) {
                      if (typeof value === 'string' && value.length > 0) {
                        return true;
                      }

                      if (Array.isArray(value) && value.length === 1) {
                        return true;
                      }
                      return false;
                    }
                  )
                  .transform((value) => {
                    if (typeof value === 'string') {
                      return value ? [value] : [];
                    }
                    return value;
                  })
              });
            } else if (
              q.customQuestionTypeId === CUSTOM_QUESTION_TYPE.MULTIPLE
            ) {
              schema[q.id] = Yup.object({
                surveyCustomQuestionChoiceIds: Yup.array()
                  .of(Yup.string())
                  .test('array-validation', '', function (value) {
                    const { path, createError } = this;
                    const error = validateArrayChoices(value, q);
                    if (error) {
                      return createError({ path, message: error });
                    }
                    return true;
                  })
              });
            } else if (
              q.customQuestionTypeId === CUSTOM_QUESTION_TYPE.OPEN_ENDED
            ) {
              schema[q.id] = Yup.object({
                answerText: buildTextValidation(q.isMandatory)
              });
            }
          }
          return schema;
        },
        {}
      );
      updateSchema(candidateSurveyCustomAnswerSchema);
    } else {
      updateSchema({});
    }
  }, [questions, updateSchema]);

  const validateArrayChoices = (value: any, question: CustomQuestion) => {
    const arrayLength = value?.length ?? 0;

    if (arrayLength === 0 && question.isMandatory) {
      return intl.formatMessage({ ...siteMessages.FIELD_REQUIRED });
    }

    if (
      question.customQuestionMultipleChoiceLimitTypeId ===
      QUESTION_CHOICE_LIMIT_TYPE.EQUAL
    ) {
      if (arrayLength !== (question.nbChoice ?? 0)) {
        return `${intl.formatMessage(
          { ...siteMessages.FIELD_EXACT_NUMBER_OPTION },
          { len: question.nbChoice }
        )}`;
      }
    }

    if (
      question.customQuestionMultipleChoiceLimitTypeId ===
      QUESTION_CHOICE_LIMIT_TYPE.LESS_OR_EQUAL
    ) {
      if (arrayLength > (question.nbChoice ?? 0)) {
        return `${intl.formatMessage(
          { ...siteMessages.FIELD_AT_MOST_NUMBER_OPTION },
          { len: question.nbChoice }
        )}`;
      }
    }

    return undefined;
  };

  const buildTextValidation = (isMandatory: boolean) => {
    let schema = Yup.string().nullable();

    if (isMandatory) {
      schema = schema
        .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
        .typeError(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }));
    }

    schema = schema
      .min(
        MIN_CHAR_OPEN_ENDED_QUESTION,
        intl.formatMessage(
          { ...siteMessages.FIELD_TOO_SHORT },
          { len: MIN_CHAR_OPEN_ENDED_QUESTION }
        )
      )
      .max(
        MAX_CHAR_OPEN_ENDED_QUESTION,
        intl.formatMessage(
          { ...siteMessages.FIELD_TOO_LONG },
          { len: MAX_CHAR_OPEN_ENDED_QUESTION }
        )
      );

    return schema;
  };

  const renderCustomQuestionByType = (q: CustomQuestion) => {
    let questionText =
      q.surveyCustomQuestionLanguages.find(
        (ql: any) =>
          ql.languageId ===
          LANGUAGES.find((l) => l.id === currentLanguageId)
            ?.localizationLanguageId
      )?.question || q.surveyCustomQuestionLanguages[0]?.question;

    const optionalText = `${
      q.isMandatory
        ? ''
        : `(${intl.formatMessage({ ...siteMessages.optional })})`
    }`;
    const options = buildOptions(q?.surveyCustomQuestionChoices || []);

    questionText = `${questionText} ${optionalText}`;

    switch (q?.customQuestionTypeId) {
      case CUSTOM_QUESTION_TYPE.BINARY:
        return (
          <YesNoButton
            name={`candidateSurveyCustomAnswers.${q?.id}.answeredYes`}
            label={questionText}
            value={values?.[q?.id ?? 0]?.answeredYes ?? null}
            onChange={(value: boolean) =>
              setFieldValue(
                `candidateSurveyCustomAnswers.${q.id}.answeredYes`,
                value
              )
            }
            error={
              typeof errors == 'string'
                ? errors
                : errors?.[q?.id || 0]?.answeredYes
            }
          />
        );
      case CUSTOM_QUESTION_TYPE.SINGLE: {
        // loaded answers are arrays, but picking an option stores a plain string
        const singleChoice =
          values?.[q?.id ?? 0]?.surveyCustomQuestionChoiceIds;
        return (
          <SelectQuestion
            controlId={`candidateSurveyCustomAnswers.${
              q?.id ?? ''
            }.surveyCustomQuestionChoiceIds`}
            label={questionText}
            isMulti={false}
            options={options}
            value={
              (Array.isArray(singleChoice) ? singleChoice[0] : singleChoice) ??
              null
            }
            error={
              typeof errors == 'string'
                ? errors
                : errors?.[q?.id ?? 0]?.surveyCustomQuestionChoiceIds
            }
            setFieldValue={setFieldValue}
          />
        );
      }

      case CUSTOM_QUESTION_TYPE.MULTIPLE:
        return (q.surveyCustomQuestionChoices?.length ?? 0) >
          MAX_MULTIPLE_CHOICE_THRESHOLD ? (
          <SelectQuestion
            controlId={`candidateSurveyCustomAnswers.${
              q?.id ?? ''
            }.surveyCustomQuestionChoiceIds`}
            label={questionText}
            isMulti
            options={options}
            value={values?.[q?.id ?? 0]?.surveyCustomQuestionChoiceIds ?? []}
            error={errors?.[q?.id ?? 0]?.surveyCustomQuestionChoiceIds}
            setFieldValue={setFieldValue}
          />
        ) : (
          <Form.Group
            controlId={`candidateSurveyCustomAnswers.${
              q?.id ?? ''
            }.surveyCustomQuestionChoiceIds`}
          >
            <Form.Label className="chk-title">{questionText}</Form.Label>
            <div className="session-availabilities-spacing">
              <Checkboxes
                checkboxId={`candidateSurveyCustomAnswers.${
                  q?.id ?? ''
                }.surveyCustomQuestionChoiceIds`}
                value={
                  values?.[q?.id ?? 0]?.surveyCustomQuestionChoiceIds ?? []
                }
                options={options}
                onChange={(value: any) =>
                  setFieldValue(
                    `candidateSurveyCustomAnswers.${
                      q?.id ?? ''
                    }.surveyCustomQuestionChoiceIds`,
                    value
                  )
                }
              />
            </div>
            {errors?.[q?.id ?? 0]?.surveyCustomQuestionChoiceIds && (
              <div className="d-flex align-items-center chk-error">
                <FiAlertTriangle className="me-1" />
                {errors?.[q.id]?.surveyCustomQuestionChoiceIds}
              </div>
            )}
          </Form.Group>
        );
      case CUSTOM_QUESTION_TYPE.OPEN_ENDED:
        return (
          <Form.Group
            controlId={`candidateSurveyCustomAnswers.${q?.id}.answerText`}
          >
            <Form.Label className="input fw-bold">{questionText}</Form.Label>
            <Form.Control
              as="textarea"
              value={values?.[q?.id ?? 0]?.answerText ?? ''}
              required={q?.isMandatory}
              onChange={(e) =>
                setFieldValue(
                  `candidateSurveyCustomAnswers.${q.id}.answerText`,
                  e.target.value
                )
              }
              isInvalid={!!errors?.[q.id]?.answerText}
            />
            <Form.Control.Feedback type="invalid">
              <FiAlertTriangle className="mb-1 me-1" />
              {errors?.[q.id]?.answerText}
            </Form.Control.Feedback>
          </Form.Group>
        );
    }
  };

  return (
    <div className="option-list">
      <div className="header">
        <img src={icon} className=" mt-n1" />
        <h3>{title}</h3>
      </div>
      {orderedQuestions?.map((q: CustomQuestion) => (
        <div key={q.id} className="option-item">
          {renderCustomQuestionByType(q)}
        </div>
      ))}
    </div>
  );
};
export default CustomQuestionSection;
